/**
 * App Config
 *
 * Application configuration using environment variables
 * Web version of mobile's appConfig
 *
 * IMPORTANT: Next.js ONLY replaces process.env.NEXT_PUBLIC_* when accessed
 * with STATIC STRING LITERALS (e.g., process.env.NEXT_PUBLIC_GATEWAY_API_ENDPOINT).
 * Dynamic access like process.env[key] will NOT work for client-side code.
 *
 * This file uses getters with direct static access to ensure proper
 * environment variable inlining at build time.
 */

export interface AppConfig {
    /** GraphQL API URL — server-aware: uses GATEWAY_API_ENDPOINT_SERVER on server, NEXT_PUBLIC_* on client */
    apiUrl: string;
    graphqlUrl: string;
    magentoGraphQlUrl: string;
    mediaUrl: string;
    isProduction: boolean;
    isDevelopment: boolean;
    storeCode: string;
    defaultLocale: string;
    defaultCurrency: string;
    signatureSecret: string;
}

/**
 * Application configuration using Object.defineProperty for lazy evaluation
 * MUST use static string literals for process.env access
 *
 * Environment variable mappings:
 * - NEXT_PUBLIC_GATEWAY_API_ENDPOINT: Main GraphQL API endpoint
 * - NEXT_PUBLIC_GRAPHQL_API_ENDPOINT: Alternative GraphQL endpoint
 * - NEXT_PUBLIC_MAGENTO_GATEWAY_API_ENDPOINT: Magento specific endpoint
 * - NEXT_PUBLIC_CDN_BASE_URL: Media/CDN URL
 */
const createAppConfig = (): AppConfig => {
    const config = {} as AppConfig;

    Object.defineProperties(config, {
        apiUrl: {
            get: () => {
                // Server-aware: use internal server URL on server, direct API on client
                const isServer = typeof window === 'undefined';
                if (isServer) {
                    return (
                        process.env.GATEWAY_API_ENDPOINT_SERVER ||
                        process.env.NEXT_PUBLIC_GATEWAY_API_ENDPOINT ||
                        process.env.NEXT_PUBLIC_GRAPHQL_API_ENDPOINT ||
                        '/graphql'
                    );
                }
                // Client: direct API call (no BFF proxy)
                return process.env.NEXT_PUBLIC_GATEWAY_API_ENDPOINT || '/graphql';
            },
            enumerable: true,
        },
        graphqlUrl: {
            get: () => {
                const isServer = typeof window === 'undefined';
                if (isServer) {
                    return (
                        process.env.GATEWAY_API_ENDPOINT_SERVER ||
                        process.env.NEXT_PUBLIC_GATEWAY_API_ENDPOINT ||
                        '/graphql'
                    );
                }
                return process.env.NEXT_PUBLIC_GATEWAY_API_ENDPOINT || '/graphql';
            },
            enumerable: true,
        },
        magentoGraphQlUrl: {
            get: () => {
                const isServer = typeof window === 'undefined';
                if (isServer) {
                    return (
                        process.env.COMMERCE_GATEWAY_API_ENDPOINT_SERVER ||
                        process.env.NEXT_PUBLIC_MAGENTO_GATEWAY_API_ENDPOINT ||
                        '/graphql'
                    );
                }
                return process.env.NEXT_PUBLIC_MAGENTO_GATEWAY_API_ENDPOINT || '/graphql';
            },
            enumerable: true,
        },
        mediaUrl: {
            get: () => process.env.NEXT_PUBLIC_CDN_BASE_URL || process.env.NEXT_PUBLIC_MEDIA_URL || '',
            enumerable: true,
        },
        isProduction: {
            get: () => process.env.NODE_ENV === 'production' || process.env.NEXT_PUBLIC_NODE_ENV === 'production',
            enumerable: true,
        },
        isDevelopment: {
            get: () => process.env.NODE_ENV === 'development',
            enumerable: true,
        },
        storeCode: {
            get: () => process.env.NEXT_PUBLIC_STORE_CODE || 'default',
            enumerable: true,
        },
        defaultLocale: {
            get: () => process.env.NEXT_PUBLIC_DEFAULT_LANGUAGE || process.env.NEXT_PUBLIC_DEFAULT_LOCALE || 'vi',
            enumerable: true,
        },
        defaultCurrency: {
            get: () => process.env.NEXT_PUBLIC_DEFAULT_CURRENCY || 'VND',
            enumerable: true,
        },
        signatureSecret: {
            get: () => process.env.SIGNATURE_SECRET || '',
            enumerable: true,
        },
    });

    return config;
};

export const appConfig: AppConfig = createAppConfig();

/**
 * Get specific config value
 */
export const getConfig = <K extends keyof AppConfig>(key: K): AppConfig[K] => {
    return appConfig[key];
};

/**
 * Hook to get Magento GraphQL URL
 * Matches mobile's useMagentoGraphQlUrl hook
 */
export const useMagentoGraphQlUrl = (): string => {
    return appConfig.magentoGraphQlUrl;
};

/**
 * Hook to get signature secret
 * Matches mobile's useSignatureSecret hook
 */
export const useSignatureSecret = (): string => {
    return appConfig.signatureSecret;
};

export default appConfig;
